import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Eye, EyeOff, LogOut, Menu, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { setDevUserId } from '../../api/client'
import { useDevUsers, useImpersonate, useLogout, useMe, useSwitchUsers } from '../../api/hooks'
import { ROLE_LABEL } from '../../lib/format'
import { Avatar, Badge } from '../ui'
import { Logo } from './Logo'

/** Account menu. Two extra powers depending on context:
 *  - Local dev mode: swap the demo user (header based).
 *  - SUPERADMIN with a real login: "visa som" – view the app as another user, server-side
 *    and audit-logged. Used for demos and support. */
export function TopBar({ onMenu }: { onMenu?: () => void }) {
  const me = useMe()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const logout = useLogout()
  const impersonate = useImpersonate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const devMode = me.data?.auth_mode === 'dev' && me.data?.provider === 'dev'
  const canSwitch = me.data?.can_switch_user === true
  const devUsers = useDevUsers(open && devMode)
  const switchUsers = useSwitchUsers(open && canSwitch && !devMode)
  const users = devMode ? devUsers.data : switchUsers.data

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (id: string) => {
    setOpen(false)
    if (devMode) {
      setDevUserId(id)
      qc.clear()
      qc.invalidateQueries()
    } else {
      impersonate.mutate(id === me.data?.id ? null : id)
    }
  }

  const doLogout = () => {
    setOpen(false)
    setDevUserId(null)
    logout.mutate(undefined, { onSettled: () => navigate('/logga-in', { replace: true }) })
  }

  const role = me.data?.role
  const impersonating = me.data?.impersonating
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line-200 bg-white px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3 lg:hidden"><button onClick={onMenu} aria-label="Meny" className="rounded-md border border-line-200 p-1.5 text-ink-500 hover:bg-navy-50 hover:text-ink-900"><Menu className="size-4" /></button><Logo compact /></div>
      <div className="hidden text-sm text-ink-500 lg:block">
        {impersonating ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-warning-500/10 px-2.5 py-1 text-warning-700"><Eye className="size-4" /> Du visar appen som <strong className="font-medium">{me.data?.name}</strong> · inloggad som {me.data?.actor_name}
            <button onClick={() => impersonate.mutate(null)} className="ml-1 underline hover:no-underline">Sluta</button>
          </span>
        ) : role === 'RADGIVARE' ? (
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-4 text-success-500" /> Du ser enbart ditt eget teams kunder och anmälningar</span>
        ) : (
          'Eventplattform – Wealth Management'
        )}
      </div>
      <div ref={ref} className="relative">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-navy-50" aria-haspopup="menu" aria-expanded={open}>
          <Avatar />
          <span className="hidden sm:block">
            <span className="block text-sm font-medium leading-tight text-ink-900">{me.data?.name ?? '…'}</span>
            <span className="block text-xs leading-tight text-ink-500">
              {role ? ROLE_LABEL[role] : ''}{me.data?.team_name ? ` · ${me.data.team_name}` : ''}
            </span>
          </span>
          <ChevronDown className="size-4 text-ink-400" />
        </button>
        {open && (
          <div className="surface animate-scale-in absolute right-0 mt-2 w-[22rem] origin-top-right p-2" role="menu">
            <div className="px-3 pb-2 pt-1.5 text-xs text-ink-500">
              <span className="block truncate font-medium text-ink-900">{me.data?.email}</span>
              Inloggad via {me.data?.provider === 'google' ? 'Google (Solvigo)' : me.data?.provider === 'microsoft' ? 'Microsoft (Söderberg & Partners)' : 'lokalt utvecklingsläge'}
            </div>
            {canSwitch && (
              <>
                <div className="mt-1 border-t border-line-100 px-3 pb-1.5 pt-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-500">{devMode ? 'Lokalt · Visa som' : 'Superadmin · Visa som'}</div>
                <div className="max-h-[40vh] overflow-y-auto">
                  {users?.map((u) => (
                    <button key={u.id} role="menuitem" onClick={() => pick(u.id)} className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-navy-50 ${u.id === me.data?.id ? 'bg-navy-50' : ''}`}>
                      <span className="flex min-w-0 items-center gap-2.5">
                        <Avatar size="sm" />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-ink-900">{u.name}</span>
                          <span className="block truncate text-xs text-ink-500">{u.team_label ?? u.email}</span>
                        </span>
                      </span>
                      <Badge tone={u.role === 'RADGIVARE' ? 'neutral' : 'navy'}>{ROLE_LABEL[u.role]}</Badge>
                    </button>
                  ))}
                </div>
                {impersonating && (
                  <button role="menuitem" onClick={() => { setOpen(false); impersonate.mutate(null) }} className="mt-1 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-ink-700 hover:bg-navy-50"><EyeOff className="size-4" /> Sluta visa som</button>
                )}
              </>
            )}
            <div className="mt-1 border-t border-line-100 pt-1">
              <button role="menuitem" onClick={doLogout} className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-ink-700 hover:bg-navy-50"><LogOut className="size-4" /> Logga ut</button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
